import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { socialApi, type YouTubeVideo } from "@/lib/socialApi";
import { trackVideoPlay } from "@/lib/analytics";
import { useLanguage } from "@/contexts/LanguageContext";
import { useHorizontalRail } from "./rail/useHorizontalRail";
import {
    RailArrows,
    RailEdgeFades,
    RailEyebrow,
    RailPagination,
    RailProgressBar,
    RailScrollbarHideStyle,
    railContainerClass,
} from "./rail/RailControls";

const HOME_VIDEO_LIMIT = 4;

interface StepDef {
    num: string;
    labelEn: string;
    key: "discover" | "design" | "build" | "launch";
    accentHue: number;
}

const STEPS: StepDef[] = [
    { num: "01", labelEn: "DISCOVER", key: "discover", accentHue: 260 },
    { num: "02", labelEn: "DESIGN", key: "design", accentHue: 190 },
    { num: "03", labelEn: "BUILD", key: "build", accentHue: 320 },
    { num: "04", labelEn: "LAUNCH", key: "launch", accentHue: 160 },
];

const useFormatDate = () => {
    const { lang } = useLanguage();
    return (iso?: string) => {
        if (!iso) return "";
        try {
            return new Intl.DateTimeFormat(lang, { year: "numeric", month: "long" }).format(
                new Date(iso),
            );
        } catch {
            return "";
        }
    };
};

const HeroVideoBlock = ({ video }: { video: YouTubeVideo }) => {
    const { t } = useTranslation();
    const formatDate = useFormatDate();
    const [playing, setPlaying] = useState(false);
    const embedSrc = `${video.embedUrl}?autoplay=1&rel=0`;

    return (
        <div className="flex flex-col">
            <div className="relative mx-auto w-full max-w-[220px] aspect-[9/16] rounded-[20px] overflow-hidden border border-white/10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)]">
                {playing ? (
                    <iframe
                        src={embedSrc}
                        title={video.title}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full border-0"
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() => {
                            trackVideoPlay(video.title, video.id);
                            setPlaying(true);
                        }}
                        aria-label={t("videos.play_aria", { title: video.title })}
                        className="absolute inset-0 w-full h-full group/thumb"
                        data-cursor="hover"
                    >
                        <img
                            src={video.thumbnail}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(.23,1,.32,1)] group-hover/thumb:scale-[1.06]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent" />
                        <span className="absolute top-3 right-3 font-v2-label text-[9px] px-2 py-0.5 rounded-full bg-background/70 backdrop-blur-sm border border-white/10 text-white/80">
                            SHORT
                        </span>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div
                                className="w-14 h-14 rounded-full flex items-center justify-center text-white transition-transform duration-300 group-hover/thumb:scale-110"
                                style={{
                                    background: "var(--gradient-accent-v2)",
                                    boxShadow: "0 12px 28px -4px hsl(260 100% 55% / 0.6)",
                                }}
                            >
                                <Play size={20} className="ml-0.5" fill="currentColor" />
                            </div>
                        </div>
                    </button>
                )}
            </div>

            <div className="mt-5 text-center">
                <h4 className="font-v2-display text-base md:text-lg text-white leading-snug line-clamp-2 mb-2">
                    {video.title}
                </h4>
                {video.description && (
                    <p className="text-xs md:text-sm text-muted-foreground leading-relaxed font-medium line-clamp-2 mb-2">
                        {video.description}
                    </p>
                )}
                <span className="font-v2-label text-[10px] text-white/40">
                    {formatDate(video.publishedAt)}
                </span>
            </div>
        </div>
    );
};

const HeroVideoSkeleton = () => (
    <div className="flex flex-col animate-pulse">
        <div className="mx-auto w-full max-w-[220px] aspect-[9/16] rounded-[20px] bg-white/5" />
        <div className="mt-5 flex flex-col items-center gap-2">
            <div className="h-3.5 w-4/5 bg-white/5 rounded" />
            <div className="h-3.5 w-3/5 bg-white/5 rounded" />
            <div className="h-2.5 w-3/4 bg-white/5 rounded mt-1" />
            <div className="h-2.5 w-2/3 bg-white/5 rounded" />
            <div className="h-2 w-1/3 bg-white/5 rounded mt-1" />
        </div>
    </div>
);

const StepCard = ({
    step,
    video,
    isLoading,
    index,
    total,
    staggerInView,
}: {
    step: StepDef;
    video: YouTubeVideo | undefined;
    isLoading: boolean;
    index: number;
    total: number;
    staggerInView: boolean;
}) => {
    const { t } = useTranslation();
    const hasVideoSlot = !!video || isLoading;
    const accent = `hsl(${step.accentHue} 90% 60%)`;
    const accentDim = `hsl(${step.accentHue} 90% 60% / 0.35)`;
    const accentSoft = `hsl(${step.accentHue} 90% 60% / 0.18)`;

    return (
        <motion.div
            className="group relative h-full"
            initial={{ opacity: 0, y: 30 }}
            animate={staggerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{
                duration: 0.55,
                delay: 0.08 * index,
                ease: [0.22, 1, 0.36, 1],
            }}
            whileHover={{ y: -4 }}
        >
            <div
                aria-hidden
                className="absolute -inset-px rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                    background: `conic-gradient(from 140deg at 50% 50%, ${accent}, transparent 35%, transparent 65%, ${accent})`,
                    padding: "1px",
                    WebkitMask:
                        "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    WebkitMaskComposite: "xor",
                    mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    maskComposite: "exclude",
                }}
            />

            <div
                className="glass-v2 rounded-[32px] p-7 md:p-8 relative overflow-hidden h-full flex flex-col"
                data-cursor="hover"
            >
                <span
                    aria-hidden
                    className="absolute -top-4 end-4 font-v2-display leading-none select-none pointer-events-none text-[120px] md:text-[140px] text-white/[0.04] tracking-tighter"
                >
                    {step.num}
                </span>

                <div
                    aria-hidden
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                        background: `radial-gradient(600px circle at 50% 0%, ${accentSoft}, transparent 60%)`,
                    }}
                />

                <div
                    aria-hidden
                    className="absolute top-0 inset-x-8 h-px opacity-60 pointer-events-none"
                    style={{
                        background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                    }}
                />

                <div className="relative flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-2">
                            <span
                                className="font-v2-label text-[9px] px-2 py-0.5 rounded-full border"
                                style={{
                                    background: accentSoft,
                                    borderColor: accentDim,
                                    color: `hsl(${step.accentHue} 90% 85%)`,
                                }}
                            >
                                {step.num}
                            </span>
                            <span className="font-v2-label text-[10px] text-white/50">
                                {t("process.step_label", { num: step.num, label: step.labelEn })}
                            </span>
                        </div>
                        {hasVideoSlot && (
                            <span className="inline-flex items-center gap-1.5 font-v2-label text-[10px] text-primary/80">
                                <Play size={9} fill="currentColor" />
                                {t("process.from_workshop")}
                            </span>
                        )}
                    </div>

                    {hasVideoSlot && (
                        <div className="mb-6">
                            {isLoading ? (
                                <HeroVideoSkeleton />
                            ) : video ? (
                                <HeroVideoBlock video={video} />
                            ) : null}
                        </div>
                    )}

                    <div className="mt-auto pt-6 flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <span className="font-v2-label text-[10px] text-white/50 tabular-nums">
                                {String(index + 1).padStart(2, "0")}
                                <span className="text-white/20 mx-1">/</span>
                                {String(total).padStart(2, "0")}
                            </span>
                            <div className="relative h-px w-10 group-hover:w-20 transition-[width] duration-500 overflow-hidden">
                                <div className="absolute inset-0 bg-white/10" />
                                <div
                                    className="absolute inset-y-0 start-0 w-0 group-hover:w-full transition-[width] duration-700 ease-out"
                                    style={{ background: accent }}
                                />
                            </div>
                        </div>
                        <div
                            className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-45"
                            style={{
                                background: accentSoft,
                                border: `1px solid ${accentDim}`,
                            }}
                        >
                            <ArrowUpRight size={14} />
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const ProcessWithVideos = () => {
    const { t } = useTranslation();
    const { dir } = useLanguage();
    const isRtl = dir === "rtl";
    const sectionRef = useRef<HTMLElement>(null);
    const staggerInView = useInView(sectionRef, { amount: 0.15, margin: "-40px" });

    const {
        railRef,
        canPrev,
        canNext,
        needsScroll,
        activeIndex,
        scrollProgress,
        scrollToIndex,
        scrollByCard,
    } = useHorizontalRail({
        itemCount: STEPS.length,
        cardSelector: "[data-step-card]",
        isRtl,
    });

    const { data, isLoading } = useQuery({
        queryKey: ["youtube-videos", HOME_VIDEO_LIMIT],
        queryFn: async () => {
            const res = await socialApi.getYouTubeVideos(HOME_VIDEO_LIMIT);
            return res.data.data;
        },
        staleTime: 30 * 60 * 1000,
        retry: 1,
    });

    const videos = (data?.videos ?? []).slice(0, HOME_VIDEO_LIMIT);

    return (
        <section ref={sectionRef} className="py-20 md:py-32 relative" id="process">
            <div className="container mx-auto px-6 relative">
                <motion.div
                    className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ amount: 0.3, margin: "-80px" }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                    <RailEyebrow>{t("process.eyebrow")}</RailEyebrow>
                    <h2 className="font-v2-display text-4xl md:text-6xl text-white mb-6">
                        {t("process.title_prefix")}{" "}
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                            {t("process.title_highlight")}
                        </span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                        {t("process.subtitle")}
                    </p>
                </motion.div>

                <RailPagination
                    itemCount={STEPS.length}
                    activeIndex={activeIndex}
                    onSelect={scrollToIndex}
                    accentHues={STEPS.map((s) => s.accentHue)}
                    ariaLabelPrefix="step"
                />
            </div>

            <motion.div
                className="relative"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.15, margin: "-40px" }}
                transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
                {needsScroll && (
                    <>
                        <RailEdgeFades isRtl={isRtl} />
                        <RailArrows
                            onPrev={() => scrollByCard(-1)}
                            onNext={() => scrollByCard(1)}
                            canPrev={canPrev}
                            canNext={canNext}
                            isRtl={isRtl}
                        />
                    </>
                )}

                <div
                    ref={railRef}
                    tabIndex={needsScroll ? 0 : -1}
                    aria-label="Process carousel"
                    className={railContainerClass(needsScroll)}
                >
                    {STEPS.map((step, i) => (
                        <div
                            key={step.num}
                            data-step-card
                            className="snap-center shrink-0 w-[85vw] sm:w-[60vw] md:w-[300px] xl:w-[285px] 2xl:w-[330px]"
                        >
                            <StepCard
                                step={step}
                                video={videos[i]}
                                isLoading={isLoading}
                                index={i}
                                total={STEPS.length}
                                staggerInView={staggerInView}
                            />
                        </div>
                    ))}
                </div>

                {needsScroll && <RailProgressBar progress={scrollProgress} />}
            </motion.div>

            <RailScrollbarHideStyle />
        </section>
    );
};

export default ProcessWithVideos;
